import { Routes, UrlSegment, UrlMatchResult } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { tenantGuard } from './core/guards/tenant.guard';
import { tenantAdminGuard } from './core/guards/tenant-admin.guard';
import { moduleCapabilityGuard } from './core/guards/module-capability.guard';
import { integracaoLegadaGuard } from './core/guards/integracao-legada.guard';
import { adminRoutes } from './admin/admin.routes';
import { environment } from '../environments/environment';

/**
 * `/ajuda` e `/ajuda/:slug` numa rota só: o mesmo componente permanece vivo ao trocar de
 * artigo (e ao trocar `/ajuda` pelo slug do primeiro), sem recarregar a lista (#930).
 */
export function ajudaMatcher(segments: UrlSegment[]): UrlMatchResult | null {
  if (segments.length === 0 || segments.length > 2 || segments[0].path !== 'ajuda') return null;
  return segments.length === 2
    ? { consumed: segments, posParams: { slug: segments[1] } }
    : { consumed: segments };
}

export const routes: Routes = [
  { path: '', redirectTo: '/auth/login', pathMatch: 'full' },

  // ─── Área Administrativa (sem tenantId, sem tenantGuard) ─────────────────────
  ...adminRoutes,

  // ─── Central de Ajuda pública (sem auth, sem tenant) — épica #197 / #23 ──────
  {
    matcher: ajudaMatcher,
    data: { help: 'ajuda' },
    loadComponent: () =>
      import('./features/ajuda/ajuda.component').then(m => m.AjudaComponent)
  },

  // ─── Demonstração de componentes (sem auth, sem tenant, sem API) — issue #430 ──
  // Caminho de validação do artefato de produção sem sessão logada. Registrada apenas
  // quando environment.demoRoutes é true (development/hmg); o build `main` não a inclui.
  // Precisa vir ANTES da rota ':tenantId', senão '_demo' seria lido como um tenantId.
  ...(environment.demoRoutes
    ? [
        {
          path: '_demo/pdf-viewer',
          loadComponent: () =>
            import('./features/_demo/pdf-viewer-demo/pdf-viewer-demo.component')
              .then(m => m.PdfViewerDemoComponent)
        }
      ]
    : []),

  {
    path: 'auth',
    children: [
      {
        path: 'login',
        loadComponent: () =>
          import('./features/auth/login/login.component').then(m => m.LoginComponent)
      },
      {
        path: 'select-company',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/auth/select-company/select-company.component').then(m => m.SelectCompanyComponent)
      }
    ]
  },
  // ─── Login URI da Amazon (sem auth, sem tenant) — issue #848 ────────────────
  // Cadastrada no Solution Provider Portal do app GOWO na Amazon — NÃO pode mudar. Path literal
  // (não ':plataforma'): esta Login URI é só da Amazon, a api resolve o manifesto por nome fixo.
  // Vem ANTES de 'integracoes/:plataforma' abaixo só por organização (os dois path não colidem:
  // segmentos diferentes), e antes de ':tenantId' por necessidade — mesma armadilha do '_demo'.
  {
    path: 'integracoes/amazon/login',
    loadComponent: () =>
      import('./features/integracoes/oauth/amazon-login.component').then(m => m.AmazonLoginComponent)
  },
  // ─── Callback OAuth das plataformas (sem auth, sem tenant) — issue #811 ──────
  // O path é cadastrado no app OAuth de cada plataforma — e, no Mercado Livre e no Shopify,
  // está cravado no próprio KRAM.SDK. NÃO pode mudar. Precisa vir ANTES da rota ':tenantId',
  // senão 'integracoes' seria lido como um tenantId — mesma armadilha do '_demo' acima. Sem
  // guards de propósito: no fluxo iniciado pela plataforma o usuário pode nunca ter tido
  // sessão no Hub.
  {
    path: 'integracoes/:plataforma',
    loadComponent: () =>
      import('./features/integracoes/oauth/oauth-callback.component').then(m => m.OauthCallbackComponent)
  },
  // ─── Página pública de retorno do OAuth (sem auth, sem tenant) — issue #813 ──
  // Destino interno de navegação do callback acima (nunca chamada direto por uma
  // plataforma) — por isso pode vir num path próprio, com um segmento extra. Mesma
  // armadilha do ':tenantId' abaixo: precisa vir antes dele.
  {
    path: 'integracoes/:plataforma/retorno',
    loadComponent: () =>
      import('./features/integracoes/oauth/oauth-retorno.component').then(m => m.OauthRetornoComponent)
  },
  // ─── Tela de vínculo do OAuth (autenticada, sem tenant) — issue #814 ────────
  // Precisa de login (authGuard) mas NÃO de tenant: a empresa ainda não foi escolhida —
  // é o que esta tela resolve. Mesma armadilha do ':tenantId' abaixo: precisa vir antes dele.
  {
    path: 'integracoes/:plataforma/vincular',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/integracoes/oauth/vincular-integracao.component').then(m => m.VincularIntegracaoComponent)
  },
  {
    path: ':tenantId',
    loadComponent: () =>
      import('./layout/app-shell/app-shell.component').then(m => m.AppShellComponent),
    canActivate: [authGuard, tenantGuard],
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        // Página inicial: Cockpit Operacional (substitui o dashboard executivo).
        path: 'dashboard',
        data: { help: 'dashboard' },
        loadComponent: () =>
          import('./features/dashboard/cockpit/cockpit.component').then(m => m.CockpitComponent)
      },
      {
        // Dashboard executivo/financeiro preservado (sem item de menu).
        path: 'dashboard-executivo',
        loadComponent: () =>
          import('./features/dashboard/dashboard.component').then(m => m.DashboardComponent)
      },
      {
        path: 'meu-perfil',
        loadComponent: () =>
          import('./features/meu-perfil/meu-perfil.component').then(m => m.MeuPerfilComponent)
      },
      {
        path: 'meu-perfil/notificacoes',
        loadComponent: () =>
          import('./features/meu-perfil/notificacoes/notificacoes.component').then(m => m.NotificacoesPreferenciasComponent)
      },
      {
        path: 'integracoes',
        redirectTo: 'integracoes/marketplaces',
        pathMatch: 'full'
      },
      {
        path: 'integracoes/marketplaces',
        data: { help: 'integracoes.marketplaces' },
        loadComponent: () =>
          import('./features/integracoes/marketplaces/marketplaces.component').then(m => m.MarketplacesComponent)
      },
      {
        path: 'integracoes/logistica',
        data: { help: 'integracoes.logistica' },
        loadComponent: () =>
          import('./features/integracoes/logistica/logistica.component').then(m => m.LogisticaComponent)
      },
      {
        path: 'logistica/solicitacoes-separacao',
        data: { help: 'logistica.solicitacoes-separacao' },
        loadComponent: () =>
          import('./features/logistica/solicitacoes-separacao/solicitacoes-separacao.component').then(m => m.SolicitacoesSeparacaoComponent)
      },
      {
        // #748/DSEP-F1 — precisa vir ANTES de ':id' (abaixo): declarada depois, 'dashboard'
        // casaria com ':id' e abriria o detalhe de uma solicitação inexistente (mesma
        // armadilha já resolvida em solicitacoes-armazenagem, linhas 129/144 deste arquivo).
        path: 'logistica/solicitacoes-separacao/dashboard',
        data: { help: 'logistica.solicitacoes-separacao.dashboard' },
        loadComponent: () =>
          import('./features/logistica/solicitacoes-separacao/dashboard/dashboard-separacao.component').then(m => m.DashboardSeparacaoComponent)
      },
      {
        path: 'logistica/solicitacoes-separacao/:id',
        data: {
          assunto: 'Separação',
          listaPath: 'logistica/solicitacoes-separacao',
          listaLabel: 'Ver todas as solicitações',
          help: 'logistica.solicitacoes-separacao',
        },
        loadComponent: () =>
          import('./features/logistica/solicitacoes-separacao/detalhe/separacao-detalhe.component').then(m => m.SeparacaoDetalheComponent)
      },
      {
        path: 'logistica/solicitacoes-armazenagem',
        data: { help: 'logistica.solicitacoes-armazenagem' },
        loadComponent: () =>
          import('./features/logistica/solicitacoes-armazenagem/solicitacoes-armazenagem.component').then(m => m.SolicitacoesArmazenagemComponent)
      },
      {
        path: 'logistica/solicitacoes-armazenagem/nova',
        loadComponent: () =>
          import('./features/logistica/solicitacoes-armazenagem/nova/nova-solicitacao-armazenagem.component').then(m => m.NovaSolicitacaoArmazenagemComponent)
      },
      {
        path: 'logistica/solicitacoes-armazenagem/dashboard',
        data: { help: 'logistica.solicitacoes-armazenagem.dashboard' },
        loadComponent: () =>
          import('./features/logistica/solicitacoes-armazenagem/dashboard/dashboard-armazenagem.component').then(m => m.DashboardArmazenagemComponent)
      },
      {
        // #684 (lote card-platform) — a tela própria de sincronização saiu; o estado
        // (tentativas/histórico/retry) já é o card unificado de plataforma na aba
        // Integração (`hub-platforms-tab`, entityType="solicitacao"). Redirect função
        // (não string com `:id`) para poder anexar `?tab=integracao` — a substituição de
        // segmento do redirectTo string só entende tokens de path, não query string.
        path: 'logistica/solicitacoes-armazenagem/:id/sincronizacao',
        redirectTo: (data) => `logistica/solicitacoes-armazenagem/${data.params['id']}?tab=integracao`,
      },
      {
        path: 'logistica/solicitacoes-armazenagem/:id',
        loadComponent: () =>
          import('./features/logistica/solicitacoes-armazenagem/detalhe/solicitacao-detalhe.component').then(m => m.SolicitacaoArmazenagemDetalheComponent)
      },
      {
        path: 'integracoes/financeiro',
        data: { help: 'integracoes.financeiro' },
        loadComponent: () =>
          import('./features/integracoes/financeiro/financeiro.component').then(m => m.FinanceiroComponent)
      },
      {
        path: 'integracoes/erp',
        data: { help: 'integracoes.erp' },
        loadComponent: () =>
          import('./features/integracoes/erp/erp.component').then(m => m.ErpComponent)
      },
      {
        path: 'integracoes/crm',
        data: { help: 'integracoes.crm' },
        loadComponent: () =>
          import('./features/integracoes/crm/crm.component').then(m => m.CrmComponent)
      },
      {
        // #685 (lote card-platform, D9) — duas listagens da mesma entidade (mesmo grid,
        // mesmo masterDetail, mesmo card de plataforma) viram uma: `/cilindros`.
        path: 'integracoes/cilindros',
        redirectTo: 'cilindros',
        pathMatch: 'full',
      },
      {
        path: 'produtos',
        data: { help: 'produtos' },
        loadComponent: () =>
          import('./features/produtos/produtos.component').then(m => m.ProdutosComponent)
      },
      {
        path: 'produtos/:id',
        loadComponent: () =>
          import('./features/produtos/produto-detalhe/produto-detalhe.component').then(m => m.ProdutoDetalheComponent)
      },
      {
        path: 'notas-fiscais',
        data: { help: 'notas-fiscais' },
        loadComponent: () =>
          import('./features/notas-fiscais/notas-fiscais.component').then(m => m.NotasFiscaisComponent)
      },

      // ─── Rotas de item da Central de Notificações v2 (issue #489) ────────────
      // O `FrontRoute` de uma notificação é congelado na gravação e nunca
      // recalculado: notificação emitida antes de a rota existir carrega URL
      // morta para sempre. Por isso as rotas nascem AGORA, com placeholder —
      // quando a tela de detalhe existir, troca-se só o `loadComponent` abaixo.
      // O `data` alimenta o placeholder (assunto do vocabulário canônico + volta
      // para a listagem).
      {
        path: 'notas-fiscais/:id',
        data: { assunto: 'Nota Fiscal', listaPath: 'notas-fiscais', listaLabel: 'Ver todas as notas fiscais' },
        loadComponent: () =>
          import('./shared/components/item-em-breve/item-em-breve.component').then(m => m.ItemEmBreveComponent)
      },
      {
        path: 'cilindros',
        data: { help: 'cilindros' },
        loadComponent: () =>
          import('./features/cilindros/cargas-cilindros.component').then(m => m.CargasCilindrosComponent)
      },
      {
        path: 'cilindros/:id',
        data: { assunto: 'Carga de Cilindros', listaPath: 'cilindros', listaLabel: 'Ver todas as cargas' },
        loadComponent: () =>
          import('./shared/components/item-em-breve/item-em-breve.component').then(m => m.ItemEmBreveComponent)
      },
      {
        path: 'pedidos',
        data: { help: 'pedidos' },
        loadComponent: () =>
          import('./features/pedidos/pedidos.component').then(m => m.PedidosComponent)
      },
      {
        path: 'pedidos/:id',
        data: { assunto: 'Pedido', listaPath: 'pedidos', listaLabel: 'Ver todos os pedidos' },
        loadComponent: () =>
          import('./features/pedidos/pedido-detalhe/pedido-detalhe.component').then(m => m.PedidoDetalheComponent)
      },
      // ─── Pessoas (antes "Clientes") ──────────────────────────────────────────
      // Representa clientes, fornecedores, transportadores, representantes,
      // motoristas etc. — segmentados por filtro/aba dentro da própria tela.
      // A `help key` permanece 'clientes' para não quebrar o artigo de ajuda.
      {
        path: 'pessoas',
        data: { help: 'clientes' },
        loadComponent: () =>
          import('./features/clientes/clientes.component').then(m => m.ClientesComponent)
      },
      {
        path: 'pessoas/novo',
        loadComponent: () =>
          import('./features/clientes/cliente-form/cliente-form.component').then(m => m.ClienteFormComponent)
      },
      {
        path: 'pessoas/:id',
        loadComponent: () =>
          import('./features/clientes/cliente-form/cliente-form.component').then(m => m.ClienteFormComponent)
      },
      // Redirects legados: preservam favoritos/links antigos de /clientes.
      { path: 'clientes',      redirectTo: 'pessoas',      pathMatch: 'full' },
      { path: 'clientes/novo', redirectTo: 'pessoas/novo', pathMatch: 'full' },
      { path: 'clientes/:id',  redirectTo: 'pessoas/:id',  pathMatch: 'full' },
      {
        path: 'perguntas',
        data: { help: 'perguntas' },
        loadComponent: () =>
          import('./features/perguntas/perguntas.component').then(m => m.PerguntasComponent)
      },
      {
        path: 'eventos',
        data: { help: 'eventos' },
        loadComponent: () =>
          import('./features/eventos/eventos.component').then(m => m.EventosComponent)
      },
      {
        path: 'monitoramento/erros-sincronismo',
        data: { help: 'monitoramento.erros-sincronismo' },
        loadComponent: () =>
          import('./features/monitoramento/erros-sincronismo/erros-sincronismo.component').then(m => m.ErrosSincronismoComponent)
      },
      // #946 — BI & Analytics (Integrações): shell com abas Visão geral e Execuções, liberado só
      // a quem tem o módulo BI (guard fail-closed). Ordem: filhos mais específicos antes do ''.
      {
        path: 'integracoes/bi',
        canActivate: [moduleCapabilityGuard('bi.integration')],
        loadComponent: () =>
          import('./features/bi/bi-shell/bi-shell.component').then(m => m.BiShellComponent),
        children: [
          {
            // Sem `help`: a aba ainda é só o espaço dos indicadores (#947), não há o que explicar.
            path: '',
            loadComponent: () =>
              import('./features/bi/bi-visao-geral/bi-visao-geral.component').then(m => m.BiVisaoGeralComponent)
          },
          {
            path: 'execucoes',
            data: { help: 'monitoramento.execucoes-integracao' },
            loadComponent: () =>
              import('./features/monitoramento/execucoes-integracao/execucoes-integracao.component').then(m => m.ExecucoesIntegracaoComponent)
          },
        ],
      },
      // Redirect legado: o Angular preserva query params e fragmento, então filtros, favoritos e
      // links de alertas para `monitoramento/execucoes-integracao?...` continuam válidos.
      { path: 'monitoramento/execucoes-integracao', redirectTo: 'integracoes/bi/execucoes', pathMatch: 'full' },
      {
        path: 'alertas',
        data: { help: 'alertas' },
        loadComponent: () =>
          import('./features/alertas/alertas.component').then(m => m.AlertasComponent)
      },
      // ─── Administração do TENANT — protegida por tenantAdminGuard ─────────────
      {
        path: 'configuracoes',
        canActivate: [tenantAdminGuard],
        data: { help: 'configuracoes' },
        loadComponent: () =>
          import('./features/configuracoes/configuracoes.component').then(m => m.ConfiguracoesComponent)
      },
      {
        // Notificações da equipe (#491): quem acompanha cada assunto, cobertura,
        // segmentação e retenção. `tenantAdminGuard` é conveniência de navegação
        // — a autorização real é a policy TenantAdmin do backend (espec §9).
        path: 'configuracoes/notificacoes-equipe',
        canActivate: [tenantAdminGuard],
        data: { help: 'configuracoes.notificacoes-equipe' },
        loadComponent: () =>
          import('./features/configuracoes/notificacoes-equipe/notificacoes-equipe.component').then(m => m.NotificacoesEquipeComponent)
      },
      {
        path: 'configuracoes/integracoes',
        canActivate: [tenantAdminGuard],
        data: { help: 'configuracoes.integracoes' },
        loadComponent: () =>
          import('./features/configuracoes/integracoes/integracoes-config.component').then(m => m.IntegracoesConfigComponent)
      },
      {
        path: 'configuracoes/integracoes/:id/detalhe',
        canActivate: [tenantAdminGuard],
        loadComponent: () =>
          import('./features/configuracoes/integracoes/detalhe/integracao-detalhe.component').then(m => m.IntegracaoDetalheComponent)
      },
      // Precisa vir ANTES de ':pluginId' (issue #816) — mesma armadilha de sempre: o
      // segmento literal some dentro do wildcard se vier depois.
      {
        path: 'configuracoes/integracoes/nova',
        canActivate: [tenantAdminGuard],
        data: { help: 'configuracoes.integracoes.nova' },
        loadComponent: () =>
          import('./features/integracoes/nova/nova-integracao.component').then(m => m.NovaIntegracaoComponent)
      },
      // Modo criação (#938): o mesmo IntegracaoDetalheComponent, sem gravar nada até "Criar".
      {
        path: 'configuracoes/integracoes/nova/:idPlugin',
        canActivate: [tenantAdminGuard],
        loadComponent: () =>
          import('./features/configuracoes/integracoes/detalhe/integracao-detalhe.component').then(m => m.IntegracaoDetalheComponent)
      },
      // Link antigo (era o popup da listagem): só redireciona, o guard sempre devolve UrlTree.
      {
        path: 'configuracoes/integracoes/:pluginId',
        canActivate: [tenantAdminGuard, integracaoLegadaGuard],
        children: []
      },
      {
        path: 'configuracoes/associacao-cadastros',
        canActivate: [tenantAdminGuard],
        loadComponent: () =>
          import('./features/configuracoes/associacao-cadastros/associacao-cadastros.component').then(m => m.AssociacaoCadastrosComponent)
      },
      {
        path: 'configuracoes/modulos',
        canActivate: [tenantAdminGuard],
        loadComponent: () =>
          import('./features/modulos/modulos.component').then(m => m.ModulosComponent)
      }
    ]
  }
];
